import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContextZustand';
import {
  hasRoutePermission,
  isEffectivePermissionsEmpty,
  canAccessStock,
  ROUTES,
  ROUTE_REQUIRED_PERMISSION,
} from '../../constants/permissions';
import { useEstablishmentStore } from '../../store/establishmentStore';
import Layout from '../layout/Layout';
import ComingSoon from '../common/ComingSoon';
import { SubscriptionRequired } from '../layout/SubscriptionRequiredBanner';

interface PermissionBasedRouteProps {
  children: React.ReactNode;
}

/**
 * Protege rotas do estabelecimento com base nas permission keys do usuário (IAM).
 *
 * Regras:
 * - Cada rota pode exigir uma chave em `ROUTE_REQUIRED_PERMISSION`; acesso só se `user.permissions` incluir essa chave.
 * - `needsSubscription === true`: apenas `/subscription`, `/settings` e onboarding até regularizar o plano.
 * - Acesso restrito por assinatura (`RESTRICTED`/`BLOCKED`/`needsSubscription`): também `/notifications` (alertas in-app e regularização). `/payment-config` fica bloqueada como o restante do painel.
 * - Sem permissões efetivas (lista vazia): tela “Em desenvolvimento” (onboarding IAM incompleto).
 * - Estoque (`/stock`): regra extra via `canAccessStock` (`stock.write` ou `stock.read` + configuração do estabelecimento).
 * - Sem autenticação: redireciona para `/login`.
 */
const PermissionBasedRoute: React.FC<PermissionBasedRouteProps> = ({ children }) => {
  const { user, isLoading, isAuthenticated } = useAuth();
  const { establishment } = useEstablishmentStore();
  const location = useLocation();

  // Aguarda carregamento da autenticação
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Não autenticado - Redireciona para login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const currentPath = location.pathname;

  const isRestrictedBySubscription =
    user.needsSubscription === true ||
    user.accessLevel === 'RESTRICTED' ||
    user.accessLevel === 'BLOCKED';

  if (isRestrictedBySubscription) {
    const allowedPaths = ['/subscription', '/settings', ROUTES.NOTIFICATIONS];
    if (!allowedPaths.includes(currentPath)) {
      return (
        <Layout title="Acesso restrito">
          <SubscriptionRequired
            title={
              user.accessReason === 'trial_expired'
                ? 'Período de avaliação encerrado'
                : 'Acesso restrito'
            }
            message="Regularize sua assinatura para liberar novamente esta área do sistema."
          />
        </Layout>
      );
    }
  }

  // Estoque: regra adicional além da permission key
  if (currentPath === ROUTES.STOCK) {
    if (!canAccessStock(user, establishment ?? null)) {
      return (
        <Layout title="Acesso Restrito">
          <ComingSoon
            title="Acesso Restrito"
            description="Você não tem acesso a este recurso. A área de estoque exige permissão de gestão ou autorização explícita do estabelecimento."
          />
        </Layout>
      );
    }
    return <>{children}</>;
  }

  const hasPermissionByKey = hasRoutePermission(currentPath, user.permissions);

  const isPendingDev = isEffectivePermissionsEmpty(user.permissions);

  // Usuário sem permissões IAM atribuídas — estado provisório na UI
  // Usa Layout para manter header e sidebar, mas exibe ComingSoon
  if (isPendingDev) {
    return (
      <Layout title="Em Desenvolvimento">
        <ComingSoon
          title="Área em desenvolvimento"
          description="As funcionalidades disponíveis dependem das permissões atribuídas à sua conta. Conclua a configuração de acessos com o administrador."
        />
      </Layout>
    );
  }

  // Sem permissão - Redireciona para a primeira rota permitida ou ComingSoon
  if (!hasPermissionByKey) {
    if (isRestrictedBySubscription) {
      return (
        <Layout title="Acesso restrito">
          <SubscriptionRequired message="Regularize sua assinatura para acessar esta página." />
        </Layout>
      );
    }

    // Sem a permission key exigida para esta rota
    return (
      <Layout title="Acesso Restrito">
        <ComingSoon
          title="Acesso Restrito"
          description="Você não tem permissão para acessar esta página."
        />
      </Layout>
    );
  }

  // Tem permissão - Renderiza o conteúdo
  if (ROUTE_REQUIRED_PERMISSION[currentPath] && !hasPermissionByKey) {
    return (
      <Layout title="Acesso Restrito">
        <ComingSoon
          title="Acesso Restrito"
          description="Você não tem permissão para acessar esta página."
        />
      </Layout>
    );
  }

  return <>{children}</>;
};

export default PermissionBasedRoute;
